import {
  ExtendedCalendarItem,
  SimpleCalendar,
} from "@/components/simple-calendar";
import { JobOrderList } from "@/hooks/services/pps/handlers/job-orders-api";
import dayjs from "dayjs";
import { JobResponse } from "@/hooks/services/pps/types/job-orders.type";
import { useGetData } from "@/hooks/services/use-query-hooks";
import { useQueryParams } from "@/hooks/use-query-params";
import { createFileRoute } from "@tanstack/react-router";
import { ViewPps } from "../utils/constants";
import { getRandomCalendarColor } from "../utils/utils";
import {
  parseFiltersFromQuery,
  parseSortsFromQuery,
  type DynamicFilter,
  type DynamicSort,
} from "@/components/search-filter-bar";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { CalendarComponent } from "./components/origin-calendar";

export const Route = createFileRoute("/(planner-screens)/planner/calendar")({
  component: RouteComponent,
});
export type stringOrDate = string | Date;

function RouteComponent() {
  const { params } = useQueryParams();

  // Read filters and sorts from URL search params (managed by parent planner.tsx)
  const dynamicFilters: DynamicFilter[] = params.filters
    ? parseFiltersFromQuery(params.filters as string)
    : [];

  const dynamicSorts: DynamicSort[] = params.sorts
    ? parseSortsFromQuery(params.sorts as string)
    : [];

  const [isNewVersion, setIsNewVersion] = useState(false);
  const [events, setEvents] = useState<ExtendedCalendarItem[]>([]);

  const { data } = useGetData<JobResponse>(
    JobOrderList({
      ...params,
      page: 1,
      pageSize: 100,
      viewId: ViewPps.calendar.id,
      ...(dynamicSorts.length > 0 && {
        encodeQuery: { sorts: params.sorts },
      }),
      ...(dynamicFilters.length > 0 && {
        encodeQuery: { filters: params.filters },
      }),
    }),
  );

  useEffect(() => {
    if (data?.data) {
      const transformBody: ExtendedCalendarItem[] = data?.data.map(
        (item: any) => ({
          id: item?.id ? item.id.toString() : "unknown_id",
          title: item.jobOrderNo,
          start: dayjs(item?.startDate).toDate(),
          end: dayjs(item?.dueDate).toDate(),
          description: item?.part?.partDescription,
          color: getRandomCalendarColor(),
          original: item,
        }),
      );

      setEvents(transformBody);
    } else {
      setEvents([]);
    }
  }, [data]);

  return (
    <div className="aero:flex aero:flex-col aero:gap-4">
      {/* Subtitle */}
      <div className="aero:flex aero:items-center aero:justify-between">
        <div className="aero:flex aero:items-center aero:gap-2">
          <span className="aero:w-[3px] aero:h-7 aero:bg-brand-primary-600 aero:rounded-full" />
          <h2 className="aero:font-bold aero:text-lg aero:leading-7 aero:text-brand-primary-600">
            Event Calendar
          </h2>
        </div>
        <Button
          size={"sm"}
          intent="secondary"
          design="solid"
          className="aero:!text-xs"
          onClick={() => setIsNewVersion(!isNewVersion)}
        >
          {isNewVersion ? "old" : "new"}
        </Button>
      </div>

      <div className="aero:h-[calc(100vh-20rem)]">
        {isNewVersion ? (
          <SimpleCalendar calendarEvents={events || []} />
        ) : (
          <CalendarComponent />
        )}
      </div>
    </div>
  );
}
